import { createSelector } from '@reduxjs/toolkit';
import { RootInitialState } from 'core/common/store';
import {
  getIsAppleMobile,
  getIsMobileDevice,
  getPlatform,
  getSystemLanguage,
} from 'core/common/store/system/selectors';
import { createDate } from 'core/common/utils/date';
import { getHumanDesignReport } from 'core/human-design/store/selectors';
import { Languages } from 'core/localization/entities';
import {
  getMoonPhase,
  getMoonSign,
  getPartnerMoonPhase,
  getPartnerMoonSign,
} from 'core/moon-compatibility/store/selectors';
import {
  getConsultationUpsellOffer,
  getDiscountConsultationUpsellOffer,
  getSelectedOffer,
  getTimeUpOffer,
} from 'core/offers/store/selectors';
import {
  getUserCurrency,
  getUserInformation,
  getUserLocation,
  getUtmAdName,
  getUtmContent,
  getUtmSource,
} from 'core/user/store/selectors';

const lowestOfferPirce = 1;

export const getFunnelConfig = (state: RootInitialState) => {
  return state.funnelConfig;
};

export const getFunnelName = (state: RootInitialState) => {
  const config = getFunnelConfig(state).config;

  if (config) {
    return config.name;
  }

  return '';
};

const getFormattedCity = (location: string | null) => {
  if (!location) {
    return '';
  }

  return location.split(',')[0];
};

const getFormattedBirthDate = (date: string, language: Languages) => {
  const dateTemplate = language === Languages.EN ? 'MMM D YYYY' : 'D MMMM YYYY';

  return createDate(date).locale(language).format(dateTemplate);
};

export const getConditionAttributes = createSelector(
  [
    getUtmSource,
    getUtmContent,
    getUtmAdName,
    getUserInformation,
    getSystemLanguage,
    getUserCurrency,
    getSelectedOffer,
    getTimeUpOffer,
    getConsultationUpsellOffer,
    getDiscountConsultationUpsellOffer,
    getMoonPhase,
    getMoonSign,
    getPartnerMoonPhase,
    getPartnerMoonSign,
    getHumanDesignReport,
    getIsAppleMobile,
    getIsMobileDevice,
    getPlatform,
    getUserLocation,
  ],
  (
    utmSource,
    utmContent,
    utmAdName,
    userProfile,
    language,
    currencyData,
    selectedOffer,
    timeUpOffers,
    consultationUpsellOffer,
    discountConsultationUpsellOffer,
    moonPhase,
    moonSign,
    partnerMoonPhase,
    partnerMoonSign,
    humanDesignReport,
    isAppleMobile,
    isMobileDevice,
    platform,
    userLocation,
  ) => {
    return {
      language,
      utmSource,
      utmContent,
      utmAdName,
      isAppleMobile,
      isMobileDevice,
      selectedOfferName: selectedOffer?.offerId ?? '',
      selectedOfferTrialPrice: selectedOffer?.trialPrice ?? lowestOfferPirce,
      selectedOfferTrialPriceWithCurrency: selectedOffer?.trialPriceWithCurrency ?? '',
      selectedOfferPrice: selectedOffer?.price ?? lowestOfferPirce,
      selectedOfferPriceWithCurrency: selectedOffer?.priceWithCurrency ?? '',
      // offer was selected by user - trial price
      selectedOfferTrialPriceWithCurrencySymbol: selectedOffer?.trialPriceWithCurrency ?? '',
      // offer was selected by user - full price
      selectedOfferPriceWithCurrencySymbol: selectedOffer?.priceWithCurrency ?? '',
      // discount offer - full price
      fullPriceDiscountOfferWithCurrencySymbol: timeUpOffers?.priceWithCurrency ?? '',
      consultationUpsellOfferPriceWithCurrencySymbol:
        consultationUpsellOffer?.priceWithCurrency ?? '',
      consultationUpsellOfferTrialPriceWithCurrencySymbol:
        consultationUpsellOffer?.trialPriceWithCurrency ?? '',
      discountConsultationUpsellOfferPriceWithCurrencySymbol:
        discountConsultationUpsellOffer?.priceWithCurrency ?? '',
      discountConsultationUpsellOfferTrialPriceWithCurrencySymbol:
        discountConsultationUpsellOffer?.trialPriceWithCurrency ?? '',
      currency: currencyData.currency,
      currencySymbol: currencyData.currencySymbol,
      locationLocale: userLocation.locale,
      moonPhase,
      moonSign,
      partnerMoonPhase,
      partnerMoonSign,
      humanDesignReport: humanDesignReport?.chartData ?? {},
      platform,
      formattedLocation: getFormattedCity(userProfile.location),
      formattedDate: getFormattedBirthDate(userProfile.date, language),
      ...userProfile,
    };
  },
);

export type ConditionAttributes = ReturnType<typeof getConditionAttributes>;
